import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { submitCustomerMessage } from '../../../api/customerService';

/**
 * 留言表单：姓名 / 手机号 / 邮箱（选填）+ 留言内容。
 * 提交走公开接口 POST /customer-service/messages，成功后由管理员在后台回复处理。
 */
export default function MessageForm() {
  const [form, setForm] = useState({ customerName: '', customerPhone: '', customerEmail: '', message: '' });
  const [error, setError] = useState('');
  const [succeeded, setSucceeded] = useState(false);

  const mutation = useMutation({
    mutationFn: submitCustomerMessage,
    onSuccess: () => setSucceeded(true),
    onError: (err) => setError(err instanceof Error ? err.message : '提交失败，请稍后重试'),
  });

  const set = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.customerName.trim() || !form.customerPhone.trim() || !form.message.trim()) {
      setError('请填写姓名、手机号和留言内容');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(form.customerPhone.trim())) {
      setError('请输入正确的 11 位手机号');
      return;
    }
    mutation.mutate({
      customerName: form.customerName.trim(),
      customerPhone: form.customerPhone.trim(),
      customerEmail: form.customerEmail.trim() || undefined,
      message: form.message.trim(),
    });
  };

  // 成功态：可继续再写一条
  if (succeeded) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-8 flex flex-col items-center text-center">
        <div className="w-14 h-14 flex items-center justify-center bg-emerald-50 rounded-2xl mb-4">
          <i className="ri-checkbox-circle-line text-emerald-500 text-3xl" />
        </div>
        <p className="text-base font-medium text-gray-800 mb-1">留言已提交</p>
        <p className="text-sm text-gray-400 mb-6">客服人员会尽快通过您留下的联系方式与您取得联系</p>
        <button
          type="button"
          onClick={() => {
            setForm({ customerName: '', customerPhone: '', customerEmail: '', message: '' });
            setSucceeded(false);
          }}
          className="px-4 py-2.5 text-sm text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 cursor-pointer transition-colors"
        >
          再写一条
        </button>
      </div>
    );
  }

  const inputCls =
    'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors';

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
      <h3 className="text-base font-semibold text-gray-900">给我们留言</h3>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 rounded-xl">
          <i className="ri-error-warning-line" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            您的姓名 <span className="text-red-400">*</span>
          </label>
          <input value={form.customerName} onChange={set('customerName')} placeholder="怎么称呼您" className={inputCls} />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            手机号 <span className="text-red-400">*</span>
          </label>
          <input value={form.customerPhone} onChange={set('customerPhone')} placeholder="方便客服联系您" className={inputCls} />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1.5">邮箱（选填）</label>
        <input value={form.customerEmail} onChange={set('customerEmail')} placeholder="name@example.com" className={inputCls} />
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1.5">
          留言内容 <span className="text-red-400">*</span>
        </label>
        <textarea
          value={form.message}
          onChange={set('message')}
          rows={4}
          placeholder="请描述您的问题或需求..."
          className={`${inputCls} resize-none`}
        />
      </div>

      <button
        type="submit"
        disabled={mutation.isPending}
        className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-xl cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <i className={mutation.isPending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
        {mutation.isPending ? '提交中...' : '提交留言'}
      </button>
    </form>
  );
}